import {
  Box,
  Button,
  ButtonGroup,
  TextField,
  InputAdornment,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { useState, useEffect, useMemo } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import * as services from "./../../services/saleng";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import AppBreadcrumbs from "../../components/AppBreadcrumbs";
import moment from "moment";
import "moment/locale/th";

export default function POListPage() {
  const navigator = useNavigate();
  const [search, setSearch] = useState("");
  const [type, setType] = useState("open");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [rowsData, setRowsData] = useState([]);
  const [loading, setLoading] = useState(false);

  const getPOs = async () => {
    setLoading(true);
    try {
      const res = await services.getPurchaseOrders();
      const resData = res.data.data;
      console.log("resData ", resData);

      const mapped = resData.map((po) => ({
        id: po.id.toString().padStart(4, "0"),
        description: po.detail || "-",
        name: po.customer?.name || "-",
        status: po.status,
        items: `${po.items?.length ?? 0} ไอเท็ม`,
        startDate: moment(po.startDate).add(543, "year").format("D MMM YYYY"),
        endDate: moment(po.endDate).add(543, "year").format("D MMM YYYY"),
      }));

      setRowsData(mapped);
    } catch (err) {
      console.error("❌ โหลด PO ล้มเหลว", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getPOs();
  }, []);

  const getMinEndDate = (start) =>
    new Date(new Date(start).setDate(new Date(start).getDate() + 1))
      .toISOString()
      .split("T")[0];

  const filteredRows = useMemo(() => {
    return rowsData.filter((row) => {
      const matchSearch =
        search === "" ||
        row.description.includes(search) ||
        row.name.includes(search) ||
        row.id.includes(search);
      const matchStatus = row.status === type;

      const matchStart = !startDate || row.startDate >= startDate;
      const matchEnd = !endDate || row.endDate <= endDate;

      return matchSearch && matchStatus && matchStart && matchEnd;
    });
  }, [rowsData, search, type, startDate, endDate]);

  const columns = [
    { field: "id", headerName: "PO #", width: 100 },
    { field: "description", headerName: "รายละเอียด", flex: 1 },
    { field: "name", headerName: "ชื่อบุคคล / ชื่อบริษัท", flex: 1 },
    {
      field: "status",
      headerName: "สถานะ",
      width: 120,
      renderCell: (p) => {
        const col =
          p.value === "open"
            ? "success"
            : p.value === "close"
              ? "error"
              : "default";
        const label =
          p.value === "open"
            ? "เปิด"
            : p.value === "close"
              ? "สิ้นสุด"
              : "ยกเลิก";
        return <Chip label={label} color={col} size="small" />;
      },
    },
    { field: "items", headerName: "สินค้า", width: 150 },
    { field: "startDate", headerName: "วันที่เริ่มต้น", width: 150 },
    { field: "endDate", headerName: "วันที่สิ้นสุด", width: 150 },
    {
      field: "actions",
      headerName: "",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          color="primary"
          onClick={() => navigator(`po_edit?id=${Number(params.row.id)}`)}
        >
          <EditIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box p={2}>
      {/* แถวบนสุด */}
      <AppBreadcrumbs />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        my={2}
      >
        <TextField
          fullWidth
          placeholder="ค้นหารายละเอียด"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ maxWidth: 500 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigator("po_create")}
          size="large"
        >
          เปิดใบเสนอราคา (PO)
        </Button>
      </Box>

      {/* แถวที่สอง */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        my={2}
        flexWrap="wrap"
        gap={2}
      >
        <ButtonGroup variant="outlined" color="success">
          <Button
            variant={type === "open" ? "contained" : "outlined"}
            onClick={() => setType("open")}
          >
            เปิด
          </Button>
          <Button
            variant={type === "close" ? "contained" : "outlined"}
            onClick={() => setType("close")}
          >
            สิ้นสุด
          </Button>
          <Button
            variant={type === "cancel" ? "contained" : "outlined"}
            onClick={() => setType("cancel")}
          >
            ยกเลิก
          </Button>
        </ButtonGroup>

        <Box display="flex" gap={2}>
          <TextField
            label="เริ่มต้น"
            type="date"
            value={startDate || ""}
            onChange={(e) => {
              setStartDate(e.target.value);
              setEndDate(null);
            }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="สิ้นสุด"
            type="date"
            value={endDate || ""}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: startDate ? getMinEndDate(startDate) : undefined,
            }}
          />
        </Box>
      </Box>

      {/* ตาราง */}
      <Box sx={{ height: 500 }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10]}
          disableSelectionOnClick
          loading={loading}
        />
      </Box>
    </Box>
  );
}
